/**
 * SETTINGS: the standing words this product runs on, as opposed to a day's work.
 *
 * There are four of them and there is nothing else on this screen. The scope a job sheet freezes,
 * which is the document a re-clean is argued from, the days we are closed, which the standing
 * schedule skips, since ops #46 the clocks "How did we do?" keeps: how long after the invoice
 * message arrived she is reminded, how long before the office is asked to call her, and the business
 * hours both keep, and since ops #31 texting: the go-live switch, off, test handsets or on, the two
 * days it stands on, the test handsets, and whether a pay link may go by text. All four are set once
 * and then left alone for months, which is exactly why none belongs anywhere a person is working: a
 * rail word under the rule at the foot, and no word at all on the phone's bottom bar.
 *
 * THE CLOCKS AND THE SWITCHES ARE AN OWNER'S TO SET (the owner, 2026-09-18: "we need to set this as
 * configuration, not hardcoded. we will tune this down the road"; and texting goes live on the
 * say-so recorded in FaithfullyKept/faithfully-kept-phone#16). Anybody at the office reads them here;
 * the form is drawn for an owner alone, and the server refuses anybody else whatever this screen
 * draws (`ownersOnly()` on `PUT /api/settings/:key`), and refuses the switch on until both days are
 * recorded, in its own sentence, which stands under "Not saved". The keys, their defaults, what each
 * accepts and the words a switch offers are the server's registry, sent with the values, so nothing
 * here spells a key: the two cards are told apart by kind, the three kinds texting brought and the
 * person who answers a text (ops #25), chosen by name from the office, being texting's alone.
 *
 * THE SCOPE HAD NO SCREEN AND THAT SHUT EVERY DOOR IN THE PRODUCT. `api/src/jobsheet/sheet.ts`
 * refuses to freeze a sheet when nothing is approved and in force for the service line, and until
 * the publish door was written nothing anywhere could put a row in that table. So on a real
 * database every cleaner opening every visit read "The office publishes the scope first", and the
 * office had nowhere to do it. The door exists now; this is the nowhere.
 *
 * TWO ACTS, NOT ONE, and the schema is what says so: publishing writes the words unapproved, and
 * approving puts a person's name against them. A draft adjudicates nothing and is invisible to
 * every door, which is the point of it, so this screen never calls one "in force" and says out
 * loud when a service line has nothing a sheet could quote.
 *
 * THE WORDS ARE NOT AUTHORED HERE. The default body is what the business publishes on the pricing
 * page, copied verbatim, and when it goes out unedited it is sent as an omission rather than as
 * text, so the server takes its own copy and stamps the page it came from. Ops holds a copy as
 * data; the published page is the contract. That is the same rule the rate card lives under, and
 * it is why there is no price on this screen and never will be.
 *
 * AND THE ADDRESS ON INVOICES AND RECEIPTS (ops #76 O3), the one setting a client reads, printed at
 * the top of every document ops renders, in a card of its own and a box with room for its four lines.
 *
 * AND CLIENT EMAIL (ops #76 O6): the go-live switch for the emails home delivers, the test addresses
 * it lets through, drawn as the handsets are, and the day DMARC reached quarantine, which `on` stands
 * on, in a card of their own. The one place this screen tells a card by key as well as by kind: a
 * switch and a day are texting's kinds too, so the email card takes its own keys by their prefix.
 *
 * AND ONE ACT THAT IS NOT A SETTING, an owner's alone: re-granting texting from the website tick
 * (ops #55, item 5), for the households booked before texting went live, whose tick was never written
 * into the register. It sits here because it is done once and then left alone, like everything else
 * on this screen, and it counts before it writes.
 *
 * OFFICE ONLY, and the server is what enforces it: `officeOnly()` sits on every /api/scopes door,
 * so a cleaner who reached this component with a patched role in her own browser gets a 403 and an
 * empty screen. CLAUDE.md binding rule 6.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  SERVICE_LINES,
  SERVICE_LINE_LABELS,
  TEXTING_REGRANT_SKIPS,
  TEXTING_REGRANT_SKIP_LABELS,
  humanise,
  type ServiceLine,
} from '@fk/shared';
import {
  ApiError,
  approveScope,
  countTextingRegrant,
  listScopes,
  listSettings,
  listUsers,
  publishScope,
  putSetting,
  regrantTexting,
  resetSetting,
  retireScope,
  type Me,
  type OfficeUser,
  type ScopeList,
  type ScopeRow,
  type SettingKind,
  type SettingRow,
  type TextingRegrantCount,
  type TextingRegrantResult,
} from '../api.ts';
import { Card, Detail, Pill, READING_WIDTH, ScreenTitle } from '../components/AppShell.tsx';
import { Button, SelectField, TextAreaField, TextField } from '../components/Field.tsx';
import { Busy, ErrorNotice, SavedNotice } from '../components/Notice.tsx';
import { formatDay, formatInstant, isDay, orNull } from '../format.ts';
import { officePeople } from '../people.ts';
import { isInForce, retireOffer, scopeStandings, type ScopeStanding } from '../scopes.ts';

export function Settings({ me }: { me: Me }) {
  const [list, setList] = useState<ScopeList | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [said, setSaid] = useState<string | null>(null);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const done = useCallback((sentence: string) => {
    setSaid(sentence);
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let live = true;
    setError(null);
    listScopes()
      .then((answer) => {
        if (live) setList(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'The scope did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  const standings: readonly ScopeStanding[] =
    list === null ? [] : scopeStandings(list.scopes, SERVICE_LINES, me.today);

  return (
    <div className={READING_WIDTH}>
      <ScreenTitle title="Settings" subtitle="The standing words this product runs on." />

      {said ? (
        <div className="mb-4">
          <SavedNotice>{said}</SavedNotice>
        </div>
      ) : null}

      <Card className="mb-4 flex flex-col gap-4">
        <div>
          <h2 className="font-serif text-lg leading-7">The job sheet's scope</h2>
          <p className="mt-1 text-sm text-navy/70">
            What a clean covers, as the client was given it. A job sheet freezes the version in
            force the first time anybody opens it, so what a cleaner reads at a door and what a
            re-clean is argued from are the same words for good. Nothing here is ever edited: a
            change is a new version, and the sheets that quoted the old one still quote what they
            showed.
          </p>
        </div>

        {error ? (
          <ErrorNotice title="The scope did not load" message={error.message} onRetry={reload} />
        ) : list === null ? (
          <Busy what="the scope" />
        ) : (
          <>
            {standings.map((standing) => (
              <ScopeLine
                key={standing.serviceLine}
                standing={standing}
                today={me.today}
                onDone={done}
              />
            ))}
            <PublishWords published={list.published} today={me.today} onDone={done} />
          </>
        )}
      </Card>

      <Card className="mb-4">
        <h2 className="font-serif text-lg leading-7">Closed days</h2>
        <p className="mt-1 mb-3 text-sm text-navy/70">
          A holiday, a storm, a day nobody is available. The standing schedule skips a closed day
          without moving anybody's weekday, and visits already booked on one are left alone: each of
          those was told to a client, so each moves from its own page with its own message.
        </p>
        <Link
          to="/closed-days"
          className="inline-flex min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-cream px-4 text-sm font-medium"
        >
          Days we are closed
        </Link>
      </Card>

      <OwnersSettings owner={me.role === 'owner'} onDone={setSaid} />

      {me.role === 'owner' ? <TextingRegrant /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Reminders and business hours (ops #46, step 2b), and texting (ops #31).
 * ------------------------------------------------------------------ */

/**
 * The kinds texting brought (ops #31), and the person who answers a text (ops #25), texting's alone;
 * the clocks are the other three.
 */
const TEXTING_KINDS: ReadonlySet<SettingKind> = new Set<SettingKind>([
  'switch',
  'date',
  'numbers',
  'user',
]);

/**
 * Which card a setting is drawn in: the clocks, texting, client email, the address documents print,
 * or the builds of the cleaners' app.
 */
type SettingCard = 'clocks' | 'texting' | 'email' | 'documents' | 'cleanerApp';

/** Client email's keys (ops #76 O6), each spelled `email_` first by the server's registry. */
const EMAIL_KEY_PREFIX = 'email_';

/** The cleaners' app's keys (ops #92), each spelled `cleaner_app.` first by the server's registry. */
const CLEANER_APP_KEY_PREFIX = 'cleaner_app.';

function cardOf(setting: Pick<SettingRow, 'key' | 'kind'>): SettingCard {
  if (setting.key.startsWith(CLEANER_APP_KEY_PREFIX)) return 'cleanerApp';
  if (setting.kind === 'addresses' || setting.key.startsWith(EMAIL_KEY_PREFIX)) return 'email';
  if (TEXTING_KINDS.has(setting.kind)) return 'texting';
  return setting.kind === 'lines' ? 'documents' : 'clocks';
}

/** The days of the week as a setting spells them, in the order a week is written. */
const WEEK: readonly { readonly word: string; readonly label: string }[] = [
  { word: 'mon', label: 'Monday' },
  { word: 'tue', label: 'Tuesday' },
  { word: 'wed', label: 'Wednesday' },
  { word: 'thu', label: 'Thursday' },
  { word: 'fri', label: 'Friday' },
  { word: 'sat', label: 'Saturday' },
  { word: 'sun', label: 'Sunday' },
];

/** What a box shows while it is empty, by kind: the shape the server's registry accepts. */
const PLACEHOLDERS: Partial<Record<SettingKind, string>> = {
  time: 'HH:MM',
  date: 'YYYY-MM-DD, then how we know',
  numbers: '+17275550123, with a comma between',
  addresses: 'someone@example.com, with a comma between',
  version: '1.2.0',
};

/** "9:00 am" from "09:00". */
function timeWords(hhmm: string): string {
  const [hours, minutes] = hhmm.split(':').map(Number) as [number, number];
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return hhmm;
  const shown = hours % 12 === 0 ? 12 : hours % 12;
  return `${shown}:${String(minutes).padStart(2, '0')} ${hours < 12 ? 'am' : 'pm'}`;
}

/**
 * A person a `user` setting names, by name and never by id. Somebody named who has since left the
 * office, or been suspended, is read as nobody by the one reader (`api/src/seams/facts.ts`), so the
 * words say so rather than a name that no longer answers anything. Only while the names did not load
 * is the value itself all there is to show.
 */
function personWords(value: string, people: readonly OfficeUser[] | null): string {
  if (value === '') return 'Nobody';
  if (people === null) return value;
  const person = people.find((one) => one.id === value);
  if (!person) return 'Somebody no longer on the sign-in list, so nobody';
  return officePeople([person]).length === 1
    ? person.displayName
    : `${person.displayName}, not in the office now, so nobody`;
}

/**
 * THE SWITCHES WHOSE WORDS ARE NOT THEIR CHOICES HUMANISED, by key. The visit reminder's lead is so
 * many days before the clean (ops #78), and a digit humanised is the bare digit: "Visit reminders: 2".
 */
const SWITCH_WORDS: Readonly<Record<string, (value: string) => string>> = {
  visit_reminder_lead_days: (value) =>
    /^\d+$/.test(value) ? `${value} ${value === '1' ? 'day' : 'days'} before` : humanise(value),
};

/**
 * A setting's value in the words a person reads. `people` is everybody `GET /api/users` answered
 * with, for a `user` setting, or null while the names have not loaded; `key` names the setting, for
 * a switch whose words are its own.
 */
export function settingWords(
  kind: SettingRow['kind'],
  value: string,
  people: readonly OfficeUser[] | null = null,
  key?: string,
): string {
  if (kind === 'user') return personWords(value, people);
  if (kind === 'hours') return value === '1' ? '1 hour' : `${value} hours`;
  if (kind === 'time') return timeWords(value);
  if (kind === 'switch') {
    const own = key === undefined ? undefined : SWITCH_WORDS[key];
    return own ? own(value) : humanise(value);
  }
  if (kind === 'date') return value === '' ? 'Not recorded' : value;
  if (kind === 'numbers' || kind === 'addresses') {
    return value === '' ? 'None listed' : value.split(',').join(', ');
  }
  if (kind === 'version') return value === '' ? 'None named' : value;
  if (kind === 'words') return value === '' ? "The app's own sentence" : value;
  if (kind === 'lines') {
    const lines = value
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line !== '');
    return lines.length === 0 ? 'Not set' : lines.join(', ');
  }
  const words = value.split(',');
  const days = WEEK.filter((day) => words.includes(day.word)).map((day) => day.label);
  return days.length === 0 ? value : days.join(', ');
}

/**
 * THE CLOCKS "HOW DID WE DO?" KEEPS, AND TEXTING'S SWITCHES, read once and drawn in two cards.
 * Silence is never taken as an answer: a reminder at one clock and a call from the office at the
 * other, each counted from when the invoice message arrived and each moved into the business day.
 * And nothing ops composes reaches anybody by text until an owner moves the go-live switch (ops #31).
 * Read by the office; set by an owner, one at a time, in the same two presses the scope's words take,
 * and the server's own sentence shown when it refuses a value.
 */
function OwnersSettings({ owner, onDone }: { owner: boolean; onDone: (sentence: string) => void }) {
  const [settings, setSettings] = useState<readonly SettingRow[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [people, setPeople] = useState<readonly OfficeUser[] | null>(null);

  /**
   * EVERYBODY WITH A SIGN-IN, asked for once, for the one setting that names a person: the picker
   * narrows it to the office by `officePeople`, and every line saying who stands reads the name from
   * it, suspended or not, so nobody ever reads an id. The door refuses anybody not in the office and
   * still here whatever this offers (binding rule 6). A list that did not arrive leaves the picker
   * offering nobody alone and saying so, rather than a screen that will not draw.
   */
  useEffect(() => {
    let live = true;
    listUsers()
      .then((answer) => {
        if (live) setPeople(answer.users);
      })
      .catch(() => {
        if (live) setPeople(null);
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    let live = true;
    setError(null);
    listSettings()
      .then((answer) => {
        if (live) setSettings(answer.settings);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'The settings did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  const saved = useCallback(
    (sentence: string) => {
      onDone(sentence);
      setAttempt((n) => n + 1);
    },
    [onDone],
  );

  /** One card's settings, in the order the server lists them, or where the one read stands. */
  const lines = (card: SettingCard, consequence: string) =>
    error ? (
      <ErrorNotice
        title="The settings did not load"
        message={error.message}
        onRetry={() => setAttempt((n) => n + 1)}
      />
    ) : settings === null ? (
      <Busy what="the settings" />
    ) : (
      <div className="flex flex-col gap-3">
        {settings
          .filter((setting) => cardOf(setting) === card)
          .map((setting) => (
            <SettingLine
              key={setting.key}
              setting={setting}
              owner={owner}
              people={people}
              consequence={consequence}
              onSaved={saved}
            />
          ))}
      </div>
    );

  return (
    <>
      <Card className="mb-4">
        <h2 className="font-serif text-lg leading-7">Reminders and business hours</h2>
        <p className="mt-1 mb-3 text-sm text-navy/70">
          Every invoice message asks how the clean went. When nobody answers, they are asked again,
          and then the office is asked to call them: silence is never taken as a good answer. Both
          are counted from when the message arrived, and each waits for the business day, in
          Florida.
        </p>
        {owner ? null : <p className="mb-3 text-sm">An owner changes these.</p>}
        {lines('clocks', 'It counts for every question not yet answered, from the next run on.')}
      </Card>

      <Card className="mb-4">
        <h2 className="font-serif text-lg leading-7">Texting</h2>
        <p className="mt-1 mb-3 text-sm text-navy/70">
          Whether ops texts anybody, and what that stands on. Nothing ops composes reaches a client
          by text until an owner moves the switch, on the say-so recorded in
          FaithfullyKept/faithfully-kept-phone#16: test handsets texts the numbers listed here and
          nobody else, and on needs both days recorded first. The phone system has a switch of its
          own, and a text goes only when both allow it.
        </p>
        {owner ? null : <p className="mb-3 text-sm">An owner changes these.</p>}
        {lines('texting', 'It counts for every text from now on, and for any still waiting to go.')}
      </Card>

      <Card className="mb-4">
        <h2 className="font-serif text-lg leading-7">Client email</h2>
        <p className="mt-1 mb-3 text-sm text-navy/70">
          Whether ops emails clients, and what that stands on. Home sends every email ops composes,
          from home@, and nothing reaches a client by email until an owner moves the switch: test
          addresses emails the addresses listed here and nobody else, and on needs the DMARC day
          recorded first.
        </p>
        {owner ? null : <p className="mb-3 text-sm">An owner changes these.</p>}
        {lines('email', 'It counts for every email from now on, and for any still waiting to go.')}
      </Card>

      <Card>
        <h2 className="font-serif text-lg leading-7">Invoices and receipts</h2>
        <p className="mt-1 mb-3 text-sm text-navy/70">
          The address printed at the top of every invoice and receipt, which the client keeps. Until
          it is set no invoice or receipt is made: each waits, and the owners are given a task
          saying so.
        </p>
        {owner ? null : <p className="mb-3 text-sm">An owner changes this.</p>}
        {lines(
          'documents',
          'It prints on every invoice and receipt made from now on; one already made keeps the address it was made with.',
        )}
      </Card>

      <Card className="mt-4">
        <h2 className="font-serif text-lg leading-7">The cleaners' app</h2>
        <p className="mt-1 mb-3 text-sm text-navy/70">
          Which builds of the cleaners' app may still be used. A build below the oldest allowed
          shows the message, lets them read what it holds and takes no new work until it is updated;
          a build below the newest says once that an update is available. The app reads these each
          time it opens.
        </p>
        {owner ? null : <p className="mb-3 text-sm">An owner changes these.</p>}
        {lines('cleanerApp', 'Each build of the app reads it the next time it opens.')}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Re-granting texting from the website tick (ops #55, item 5).
 * ------------------------------------------------------------------ */

/** "1 person" or "3 people". */
const peopleWords = (count: number): string => (count === 1 ? '1 person' : `${count} people`);

/** What a press would do, said before anybody presses. */
function regrantCountWords(count: TextingRegrantCount): string {
  if (count.toGrant === 0) {
    return 'Nobody is waiting for a grant: everybody whose own tick counts has one already, or is passed by for a reason below.';
  }
  const who = `${peopleWords(count.toGrant)} would be granted texting consent from their own tick.`;
  return count.toGrant > count.perPress
    ? `${who} One press grants at most ${count.perPress}; press again for the rest.`
    : who;
}

/** What a press did. */
function regrantResultWords(result: TextingRegrantResult): string {
  const granted = `Granted ${result.granted}.`;
  return result.remaining > 0
    ? `${granted} ${result.remaining} more wait for another press.`
    : granted;
}

/** Each reason that passed somebody by, with how many, in the order the rule asks them. */
function PassedBy({ skipped }: { skipped: TextingRegrantCount['skipped'] }) {
  const reasons = TEXTING_REGRANT_SKIPS.filter((reason) => (skipped[reason] ?? 0) > 0);
  if (reasons.length === 0) return null;
  return (
    <ul className="mt-2 list-disc pl-5 text-sm">
      {reasons.map((reason) => (
        <li key={reason}>{`${TEXTING_REGRANT_SKIP_LABELS[reason]}: ${skipped[reason]}`}</li>
      ))}
    </ul>
  );
}

/**
 * RE-GRANT TEXTING FROM THE WEBSITE TICK (ops #55, item 5), an owner's alone. Households booked
 * before texting went live ticked the box on the website quote form, and nothing wrote the tick into
 * the register, so none of them could be texted. The count comes first and writes nothing: how many a
 * press would grant, and why the rest are passed by. The press asks once more, then grants at most one
 * press's worth, each from the box she ticked, audited as the owner who pressed, and sends nothing;
 * the count is read again after it. Drawn for an owner alone, and never even asked for anybody else;
 * the server refuses anybody else whatever this draws (`ownersOnly()` on both doors, binding rule 6).
 */
function TextingRegrant() {
  const [count, setCount] = useState<TextingRegrantCount | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [result, setResult] = useState<TextingRegrantResult | null>(null);

  useEffect(() => {
    let live = true;
    setError(null);
    countTextingRegrant()
      .then((answer) => {
        if (live) setCount(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'The count did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [attempt]);

  async function run() {
    setBusy(true);
    setProblem(null);
    try {
      const answer = await regrantTexting();
      setAsking(false);
      setResult(answer);
      setAttempt((n) => n + 1);
    } catch (thrown) {
      setAsking(false);
      setProblem(thrown instanceof ApiError ? thrown.message : 'Nothing was granted.');
    } finally {
      setBusy(false);
    }
  }

  const thisPress = count === null ? 0 : Math.min(count.toGrant, count.perPress);

  return (
    <Card className="mt-4">
      <h2 className="font-serif text-lg leading-7">Re-grant texting from the website tick</h2>
      <p className="mt-1 mb-3 text-sm text-navy/70">
        Households booked before texting went live ticked the box on the website quote form, and the
        tick was never written down as consent to texts, so none of them can be texted. This writes
        that consent once for each person on a household whose own tick is for the number they hold
        now, dated when they ticked it. It never overrides a later no: a withdrawal, a later form
        left unticked, a do-not-contact mark or a STOP. Nothing is sent to anybody.
      </p>

      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not granted" message={problem} />
        </div>
      ) : null}

      {result ? (
        <div className="mb-3">
          <SavedNotice>{regrantResultWords(result)}</SavedNotice>
          <PassedBy skipped={result.skipped} />
        </div>
      ) : null}

      {error ? (
        <ErrorNotice
          title="The count did not load"
          message={error.message}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      ) : count === null ? (
        <Busy what="the count" />
      ) : (
        <>
          <p className="text-sm">{regrantCountWords(count)}</p>
          <PassedBy skipped={count.skipped} />
          {count.toGrant === 0 ? null : asking ? (
            <div className="mt-3 rounded-lg border border-navy/20 p-3">
              <p className="text-sm">
                Grant texting consent to {peopleWords(thisPress)}, each from the box they ticked on
                the website form? Nothing is sent to anybody.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button disabled={busy} onClick={() => void run()}>
                  {busy ? 'Granting' : 'Yes, re-grant texting'}
                </Button>
                <Button tone="quiet" onClick={() => setAsking(false)} disabled={busy}>
                  Never mind
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-3">
              <Button
                tone="quiet"
                onClick={() => {
                  setProblem(null);
                  setAsking(true);
                }}
              >
                Re-grant texting
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * One setting: what stands and who set it, and for an owner the control to change it, a list of the
 * words the server sends for a switch, the days of the week as boxes to tick, the office by name for
 * a person, and a box with the server's hint for the rest.
 *
 * AND THE WAY BACK TO THE DEFAULT (ops #54, item 8), on a value an owner set and no other: typing the
 * default's value by hand kept a row, which a later change of the default never reached, and the line
 * went on saying who set it. Asked once more, like a change, with what it goes back to; the server
 * withdraws the row, audited as her, and refuses in its own words where the default would break a pair.
 */
function SettingLine({
  setting,
  owner,
  people,
  consequence,
  onSaved,
}: {
  setting: SettingRow;
  owner: boolean;
  /** Everybody with a sign-in, for a setting that names a person; null while they have not loaded. */
  people: readonly OfficeUser[] | null;
  /** What a change does, in the card's words, said before it is made. */
  consequence: string;
  onSaved: (sentence: string) => void;
}) {
  const words = (value: string) => settingWords(setting.kind, value, people, setting.key);
  const standing = setting.value ?? setting.default;
  const [draft, setDraft] = useState(standing);
  const [asking, setAsking] = useState(false);
  /** Asking once more before the value goes back to the default. */
  const [resetting, setResetting] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const id = `setting-${setting.key}`;

  const who =
    setting.value === null
      ? 'The default'
      : `Set by ${setting.updatedByName ?? 'an owner'}, ${formatInstant(setting.updatedAt) ?? ''}`;

  async function save() {
    setBusy(true);
    setProblem(null);
    try {
      const { setting: now } = await putSetting(setting.key, draft);
      setAsking(false);
      onSaved(
        `Saved. ${now.label}: ${settingWords(now.kind, now.value ?? now.default, people, now.key)}.`,
      );
    } catch (thrown) {
      setAsking(false);
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not saved.');
    } finally {
      setBusy(false);
    }
  }

  async function backToDefault() {
    setBusy(true);
    setProblem(null);
    try {
      const { setting: now } = await resetSetting(setting.key);
      setResetting(false);
      // The line is keyed by the setting, so the list reloading never remounts it: the box has to be
      // brought to the default itself, or it goes on showing the value just withdrawn, with Save live.
      setDraft(now.value ?? now.default);
      setAsking(false);
      onSaved(
        `Back to the default. ${now.label}: ${settingWords(now.kind, now.value ?? now.default, people, now.key)}.`,
      );
    } catch (thrown) {
      setResetting(false);
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not saved.');
    } finally {
      setBusy(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    if (draft.trim() === standing) return;
    setResetting(false);
    setAsking(true);
  }

  if (!owner) {
    return (
      <dl>
        <Detail label={setting.label} value={`${words(standing)}. ${who}.`} />
      </dl>
    );
  }

  const days = draft.split(',').map((word) => word.trim());
  const toggle = (word: string) => {
    const next = days.includes(word) ? days.filter((one) => one !== word) : [...days, word];
    setDraft(
      WEEK.filter((day) => next.includes(day.word))
        .map((day) => day.word)
        .join(','),
    );
    setAsking(false);
  };

  return (
    <form className="rounded-lg border border-navy/15 p-3" onSubmit={submit} noValidate>
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not saved" message={problem} />
        </div>
      ) : null}

      {setting.kind === 'days' ? (
        <fieldset className="mb-4">
          <legend className="text-sm font-medium">{setting.label}</legend>
          <div className="mt-1 flex flex-wrap gap-x-4">
            {WEEK.map((day) => (
              <label
                key={day.word}
                className="inline-flex min-h-11 desk:min-h-9 items-center gap-2 text-sm"
              >
                <input
                  type="checkbox"
                  id={`${id}-${day.word}`}
                  checked={days.includes(day.word)}
                  onChange={() => toggle(day.word)}
                />
                {day.label}
              </label>
            ))}
          </div>
          <p className="mt-1 text-xs text-navy/65">{setting.hint}</p>
        </fieldset>
      ) : setting.kind === 'switch' ? (
        <SelectField
          id={id}
          label={setting.label}
          value={draft}
          onChange={(value: string) => {
            setDraft(value);
            setAsking(false);
          }}
          options={setting.choices ?? [standing]}
          toLabel={(value) => settingWords('switch', value, null, setting.key)}
          hint={setting.hint}
        />
      ) : setting.kind === 'user' ? (
        <PersonPicker
          id={id}
          label={setting.label}
          value={draft}
          standing={standing}
          people={people}
          onChange={(value) => {
            setDraft(value);
            setAsking(false);
          }}
          hint={setting.hint}
        />
      ) : setting.kind === 'lines' ? (
        <TextAreaField
          id={id}
          label={setting.label}
          value={draft}
          rows={4}
          onChange={(value) => {
            setDraft(value);
            setAsking(false);
          }}
          hint={setting.hint}
        />
      ) : (
        <TextField
          id={id}
          label={setting.kind === 'hours' ? `${setting.label}, in hours` : setting.label}
          value={draft}
          inputMode={setting.kind === 'hours' ? 'numeric' : 'text'}
          placeholder={PLACEHOLDERS[setting.kind]}
          onChange={(value) => {
            setDraft(value);
            setAsking(false);
          }}
          hint={setting.hint}
        />
      )}

      <p className="mb-3 text-xs text-navy/65">
        {who}. Stands at {words(standing)}
        {setting.value === null ? '' : `; the default is ${words(setting.default)}`}.
      </p>

      {asking ? (
        <div className="rounded-lg border border-navy/20 p-3">
          <p className="text-sm">
            Change {setting.label.toLowerCase()} from {words(standing)} to {words(draft.trim())}?{' '}
            {consequence}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button disabled={busy} onClick={() => void save()}>
              {busy ? 'Saving' : 'Yes, change it'}
            </Button>
            <Button tone="quiet" onClick={() => setAsking(false)} disabled={busy}>
              Never mind
            </Button>
          </div>
        </div>
      ) : resetting ? (
        <div className="rounded-lg border border-navy/20 p-3">
          <p className="text-sm">
            Put {setting.label.toLowerCase()} back to the default, {words(setting.default)}, from{' '}
            {words(standing)}? {consequence}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button disabled={busy} onClick={() => void backToDefault()}>
              {busy ? 'Saving' : 'Yes, back to the default'}
            </Button>
            <Button tone="quiet" onClick={() => setResetting(false)} disabled={busy}>
              Never mind
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button type="submit" tone="quiet" disabled={busy || draft.trim() === standing}>
            Save {setting.label.toLowerCase()}
          </Button>
          {setting.value === null ? null : (
            <Button
              tone="quiet"
              disabled={busy}
              onClick={() => {
                setProblem(null);
                setResetting(true);
              }}
            >
              Back to the default
            </Button>
          )}
        </div>
      )}
    </form>
  );
}

/**
 * A PERSON IN THE OFFICE, BY NAME (ops #25): nobody first, because an unassigned follow-up is a real
 * answer and every owner is told of every text either way, then the office as `officePeople` narrows
 * everybody with a sign-in. Somebody named before she left the office stays in the list under words
 * saying she answers nothing now, so the picker shows what stands and nobody can still be chosen.
 */
function PersonPicker({
  id,
  label,
  value,
  standing,
  people,
  onChange,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  standing: string;
  people: readonly OfficeUser[] | null;
  onChange: (value: string) => void;
  hint: string;
}) {
  const office = officePeople(people ?? []).map((person) => person.id);
  const options = standing === '' || office.includes(standing) ? office : [...office, standing];
  return (
    <SelectField
      id={id}
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      toLabel={(chosen) => settingWords('user', chosen, people)}
      placeholder="Nobody"
      hint={
        people === null
          ? 'The names in the office did not load, so only nobody can be chosen now. Reload the page to choose a person.'
          : hint
      }
    />
  );
}

/* ------------------------------------------------------------------ *
 * One service line, and every version of its words.
 * ------------------------------------------------------------------ */

function ScopeLine({
  standing,
  today,
  onDone,
}: {
  standing: ScopeStanding;
  today: string;
  onDone: (sentence: string) => void;
}) {
  const { inForce, ahead, drafts, superseded, tailored, tailoredHomes } = standing;

  return (
    <section className="rounded-lg border border-navy/15 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-serif text-base leading-tight">
          {SERVICE_LINE_LABELS[standing.serviceLine]}
        </h3>
        {inForce ? (
          <Pill tone="sage">In force, version {inForce.version}</Pill>
        ) : (
          <Pill tone="coral">Nothing a job sheet can quote</Pill>
        )}
      </div>

      {inForce ? (
        <>
          <dl className="mt-2 md:grid md:grid-cols-2 md:gap-x-3">
            <Detail label="In force from" value={formatDay(inForce.effectiveFrom)} />
            <Detail label="Approved" value={formatInstant(inForce.approvedAt)} />
            <Detail
              label="Where the client read it"
              value={inForce.sourceUrl ?? 'Typed here, as it was agreed'}
            />
            <Detail label="Earlier versions" value={supersededWords(superseded.length)} />
          </dl>
          <TheWords idPrefix={`in-force-${standing.serviceLine}`} scope={inForce} />
          <Retire standing={standing} scope={inForce} today={today} onDone={onDone} />
        </>
      ) : (
        <p className="mt-2 text-sm">
          No version of these words is approved and in force, so no job sheet can be opened for this
          service line at all. Publish the words below, then approve them.
        </p>
      )}

      {ahead.map((scope) => (
        <div key={scope.id} className="mt-3 rounded-lg border border-navy/15 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Version {scope.version}</span>
            <Pill tone="gold">Takes over on {formatDay(scope.effectiveFrom)}</Pill>
          </div>
          <TheWords idPrefix={`ahead-${scope.id}`} scope={scope} />
        </div>
      ))}

      {drafts.map((scope) => (
        <Draft key={scope.id} scope={scope} onDone={onDone} />
      ))}

      {/* HOMES, NOT VERSIONS, and only the ones a door can see. A home whose words have been
          revised is still one home, and a version nobody has approved is quoted nowhere at all. */}
      {tailoredHomes > 0 ? (
        <p className="mt-3 text-xs text-navy/65">
          {tailoredHomes === 1 ? 'One home has' : `${tailoredHomes} homes have`} words of their own
          for this service line, and those beat the standard ones at that home.
        </p>
      ) : null}

      {/*
       * AND THEY ARE LISTED, WHICH THEY WERE NOT, because until a version could be ended there was
       * nothing to do about one and a count was enough. A tailored version outranks the standard
       * words at its home for as long as it is in force, and with no end that was for ever: the
       * thing the office needs is not the number, it is the row, with the control on it.
       */}
      {tailored.map((scope) => (
        <div key={scope.id} className="mt-3 rounded-lg border border-navy/15 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Version {scope.version}</span>
            <Pill>For one home</Pill>
            {isInForce(scope, today) ? <Pill tone="sage">In force at that home</Pill> : null}
          </div>
          <dl className="mt-1">
            <Detail label="Home" value={scope.siteId} />
            <Detail label="In force from" value={formatDay(scope.effectiveFrom)} />
            <Detail label="Stops on" value={formatDay(scope.effectiveTo)} />
          </dl>
          <TheWords idPrefix={`tailored-${scope.id}`} scope={scope} />
          <Retire standing={standing} scope={scope} today={today} onDone={onDone} />
        </div>
      ))}
    </section>
  );
}

/**
 * ENDING A VERSION, IN TWO PRESSES, and the refusal said out loud rather than left as an absence.
 *
 * Retiring is the only thing this table can do to an existing row and it is still not an edit: it
 * writes the day the words stop applying and touches nothing else, so a sheet frozen while they
 * were in force goes on quoting exactly what it showed. That is the whole design of the table and
 * it is why the confirming sentence says so every time.
 *
 * WHERE IT CANNOT BE DONE, THE REASON IS DRAWN INSTEAD OF THE BUTTON. A missing control is a
 * question somebody asks Alex about; a sentence saying nobody has approved these words, or that
 * they already stop on a day, is the answer to it.
 */
function Retire({
  standing,
  scope,
  today,
  onDone,
}: {
  standing: ScopeStanding;
  scope: ScopeRow;
  today: string;
  onDone: (sentence: string) => void;
}) {
  const offer = retireOffer(standing, scope, today);
  const [asking, setAsking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!offer.can || offer.effectiveTo === null) {
    return <p className="mt-2 text-xs text-navy/65">{offer.why}</p>;
  }

  async function run(effectiveTo: string) {
    setBusy(true);
    setProblem(null);
    try {
      await retireScope(scope.id, effectiveTo);
      setAsking(false);
      onDone('Retired. Today is the last day those words are quoted by a new job sheet.');
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'Those words were not retired.');
    } finally {
      setBusy(false);
    }
  }

  const day = offer.effectiveTo;

  return (
    <div className="mt-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not retired" message={problem} />
        </div>
      ) : null}

      {asking ? (
        <div className="rounded-lg border border-navy/20 p-3">
          <p className="text-sm">{offer.question}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button tone="quiet" disabled={busy} onClick={() => void run(day)}>
              {busy ? 'Retiring' : 'Yes, retire these words'}
            </Button>
            <Button tone="quiet" onClick={() => setAsking(false)} disabled={busy}>
              Never mind
            </Button>
          </div>
        </div>
      ) : (
        <Button tone="quiet" onClick={() => setAsking(true)}>
          {offer.words}
        </Button>
      )}
    </div>
  );
}

function supersededWords(count: number): string | null {
  if (count === 0) return null;
  return count === 1 ? 'One, kept for the sheets that quoted it' : `${count}, each kept as it was`;
}

/**
 * A version nobody has approved. It adjudicates nothing and no door can see it, so the one thing
 * worth doing to it is putting a name against it, and that takes two presses rather than one: an
 * approved version is what a re-clean is argued from, and it can never be taken back.
 */
function Draft({ scope, onDone }: { scope: ScopeRow; onDone: (sentence: string) => void }) {
  const [asking, setAsking] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(work: () => Promise<unknown>, sentence: string) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      onDone(sentence);
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 rounded-lg border border-gold/50 border-l-4 border-l-gold p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">Version {scope.version}</span>
        <Pill tone="gold">Not approved yet</Pill>
        {scope.siteId ? <Pill>For one home</Pill> : null}
      </div>
      <p className="mt-1 text-sm text-navy/70">
        Written on {formatDay(scope.effectiveFrom)} and adjudicating nothing. No job sheet can quote
        it until somebody's name is against it.
      </p>

      <TheWords idPrefix={`draft-${scope.id}`} scope={scope} />

      {problem ? (
        <div className="mt-3">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}

      <div className="mt-3">
        {asking ? (
          <div className="rounded-lg border border-navy/20 p-3">
            <p className="text-sm">
              Approve version {scope.version}? Every sheet frozen after this quotes these words, and
              a change to them is a new version rather than an edit to this one.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                disabled={busy}
                onClick={() =>
                  void run(
                    () => approveScope(scope.id),
                    'Approved. Job sheets quote these words from now on.',
                  )
                }
              >
                {busy ? 'Recording' : 'Yes, these are the words'}
              </Button>
              <Button tone="quiet" onClick={() => setAsking(false)} disabled={busy}>
                Never mind
              </Button>
            </div>
          </div>
        ) : (
          <Button tone="quiet" onClick={() => setAsking(true)}>
            Approve these words
          </Button>
        )}
      </div>
    </div>
  );
}

/** The words themselves, folded away. They run to twenty lines and they are read rarely. */
function TheWords({ idPrefix, scope }: { idPrefix: string; scope: ScopeRow }) {
  return (
    <details className="mt-2">
      <summary className="min-h-11 desk:min-h-9 cursor-pointer text-sm font-medium">
        Read the words, version {scope.version}
      </summary>
      <p
        id={`${idPrefix}-body`}
        className="mt-2 max-h-96 overflow-y-auto rounded-lg border border-navy/15 bg-cream p-3 text-sm whitespace-pre-line"
      >
        {scope.body}
      </p>
    </details>
  );
}

/* ------------------------------------------------------------------ *
 * Publishing a new version.
 * ------------------------------------------------------------------ */

function PublishWords({
  published,
  today,
  onDone,
}: {
  published: readonly { readonly serviceLine: ServiceLine; readonly body: string }[];
  today: string;
  onDone: (sentence: string) => void;
}) {
  const wordsFor = useCallback(
    (line: ServiceLine): string => published.find((row) => row.serviceLine === line)?.body ?? '',
    [published],
  );

  const [line, setLine] = useState<ServiceLine>('house_cleaning');
  const [body, setBody] = useState(() => wordsFor('house_cleaning'));
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [open, setOpen] = useState(false);

  /** Changing the line brings its published words with it, unless somebody has typed her own. */
  function chooseLine(next: ServiceLine) {
    const untouched = body.trim() === '' || body === wordsFor(line);
    setLine(next);
    setAsking(false);
    if (untouched) setBody(wordsFor(next));
  }

  /**
   * THE FIRST PRESS WRITES NOTHING. A version is permanent the moment it exists: there is no
   * delete door on `/api/scopes`, and the version number a spare draft spends is spent for good.
   * Retiring one ends it and does not remove it, so a stray draft cannot even be tidied away that
   * way. So the same two-press shape Approve uses sits here, and the second press is the only
   * thing that reaches the server.
   */
  function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    if (!body.trim()) {
      setProblem(
        'Type the words as the client was given them. There is no published text for this service line, so there is nothing to copy.',
      );
      return;
    }
    if (effectiveFrom && !isDay(effectiveFrom)) {
      setProblem('The day these words take effect has to be a real date.');
      return;
    }
    setAsking(true);
  }

  async function publish() {
    setBusy(true);
    setProblem(null);
    try {
      await publishScope({
        serviceLine: line,
        // UNEDITED PUBLISHED WORDS GO AS AN OMISSION rather than as text, so the server takes its
        // own copy and stamps the page they were published on. Sending the same characters back
        // would record words that came from nowhere, and the page a dispute is argued against is
        // the whole reason that column exists.
        body: body === wordsFor(line) ? null : body,
        effectiveFrom: orNull(effectiveFrom),
      });
      // BACK TO WHERE IT STARTED, and folded away. What was typed stays on the screen otherwise,
      // over a saved sentence at the top of a long page, and the next press writes a second
      // version of the same words that nothing in this product can ever take back.
      setAsking(false);
      setBody(wordsFor(line));
      setOpen(false);
      onDone('Published. Nothing quotes it until you approve it, which is the next step above.');
    } catch (thrown) {
      setAsking(false);
      setProblem(thrown instanceof ApiError ? thrown.message : 'Those words were not published.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <details
      className="rounded-lg border border-navy/15 p-3"
      open={open}
      onToggle={(event) => setOpen((event.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="min-h-11 desk:min-h-9 cursor-pointer font-serif text-base">
        Publish new words
      </summary>
      <form className="mt-3" onSubmit={submit} noValidate>
        {problem ? (
          <div className="mb-3">
            <ErrorNotice title="Not published" message={problem} />
          </div>
        ) : null}

        <SelectField
          id="scope-line"
          label="Which service"
          value={line}
          onChange={chooseLine}
          options={SERVICE_LINES}
          toLabel={(value) => SERVICE_LINE_LABELS[value]}
          hint="House cleaning starts from the words the business publishes. The other two are quoted from a walkthrough, so their words are typed as they were agreed."
        />

        <TextAreaField
          id="scope-body"
          label="What a clean covers"
          value={body}
          onChange={setBody}
          rows={16}
          hint="The client's copy, word for word. Left exactly as the published page has it, this is recorded as a copy of that page; changed at all, it is recorded as our own words with nothing claimed about where they came from."
        />

        <TextField
          id="scope-from"
          label="In force from"
          type="date"
          value={effectiveFrom}
          onChange={setEffectiveFrom}
          hint="Today, unless these words were agreed to start on a later day."
        />

        <p className="mb-4 text-xs text-navy/65">
          This writes the version and nothing more. It quotes nothing and judges nothing until
          somebody approves it above, which is a second act on purpose: an unapproved scope must
          never settle an argument about a clean.
        </p>

        {asking ? (
          <div className="rounded-lg border border-navy/20 p-3">
            <p className="text-sm">
              Write a new version of the {SERVICE_LINE_LABELS[line].toLowerCase()} words, in force
              from {formatDay(effectiveFrom) ?? 'today'}? A version cannot be edited, taken back or
              removed, so a spare one sits on this screen for good.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button disabled={busy} onClick={() => void publish()}>
                {busy ? 'Publishing' : 'Yes, publish these words'}
              </Button>
              <Button tone="quiet" onClick={() => setAsking(false)} disabled={busy}>
                Never mind
              </Button>
            </div>
          </div>
        ) : (
          <Button type="submit" full disabled={busy}>
            Publish these words
          </Button>
        )}
      </form>
    </details>
  );
}
